import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Grid, Chip, Avatar, Button, Tooltip, IconButton, Badge, Collapse } from '@mui/material';
import { keyframes } from '@mui/system';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BarChart } from '@mui/x-charts/BarChart';
import { selectActiveEntity, selectActiveEntityId } from '../redux/slices/orgSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetDashboard, GetActionRequired } from '../Api/Api';
import Loader from '../components/Loader';
import { avatarTone } from '../utils/format';
import MyAssignedLocations from '../components/MyAssignedLocations';
import { LoadAlert } from '../components/uiKit';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_BORDER = '#B3D3E8';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#D0E5F3' } };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: `0 2px 6px ${PRIMARY}40`, textTransform: 'none', '&:hover': { bgcolor: '#0E4A75' } };
const ghostBtn = { color: '#475569', bgcolor: '#fff', border: '1px solid #E5E7EB', fontWeight: 700, borderRadius: '7px', textTransform: 'none', '&:hover': { bgcolor: '#F8FAFC' } };

const fadeUp = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; }`;
const enter = (i) => ({ animation: `${fadeUp} .5s cubic-bezier(.22,1,.36,1) both`, animationDelay: `${i * 0.05}s` });

const initials = (s = '') => s.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const PALETTE = ['#135F93', '#0EA5E9', '#F59E0B', '#16A34A', '#E11D48', '#0E4A75', '#0891B2'];
const colorFor = (s = '') => PALETTE[(s.charCodeAt(0) || 0) % PALETTE.length];
const n = (v) => Number(v) || 0;

const ROLE_ORDER = ['Master Admin', 'Admin', 'Employee'];
const toBreakdown = (v) => {
    let arr = [];
    if (Array.isArray(v)) {
        arr = v.map((x) => ({
            label: x.role ?? x.roleName ?? x.userType ?? x.label ?? x.name ?? '',
            count: n(x.count ?? x.value ?? x.total ?? 0),
        }));
    } else if (v && typeof v === 'object') {
        arr = Object.entries(v).map(([label, count]) => ({ label, count: n(count) }));
    }
    return arr
        .filter((x) => x.label)
        .sort((a, b) => {
            const ai = ROLE_ORDER.indexOf(a.label);
            const bi = ROLE_ORDER.indexOf(b.label);
            if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            return b.count - a.count;
        });
};

function RoleBreakdown({ items, color }) {
    if (!items?.length) return null;
    const MAX = 4;
    const shown = items.slice(0, MAX);
    const rest = items.slice(MAX);
    const pill = { display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.3, borderRadius: '6px', bgcolor: '#fff', border: `1px solid ${color}33`, maxWidth: '100%' };
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1.3 }}>
            {shown.map((it) => (
                <Box key={it.label} sx={pill}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 92 }}>{it.label}</Typography>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color }}>{it.count}</Typography>
                </Box>
            ))}
            {rest.length > 0 && (
                <Tooltip arrow title={rest.map((r) => `${r.label}: ${r.count}`).join('  ·  ')}>
                    <Box sx={{ ...pill, cursor: 'default' }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color }}>+{rest.length}</Typography>
                    </Box>
                </Tooltip>
            )}
        </Box>
    );
}

const fmtClock = (t) => {
    if (!t) return '—';
    const [h, m] = String(t).split(':').map(Number);
    if (!Number.isFinite(h)) return String(t);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const clockToDate = (v) => {
    if (!v) return null;
    const s = String(v);
    const hhmm = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (hhmm) {
        const d = new Date();
        d.setHours(Number(hhmm[1]), Number(hhmm[2]), Number(hhmm[3] || 0), 0);
        return d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const compactInr = (v) => {
    const x = n(v);
    const a = Math.abs(x);
    if (a >= 1e7) return `₹${(x / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
    if (a >= 1e5) return `₹${(x / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
    if (a >= 1e3) return `₹${(x / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K`;
    return `₹${Math.round(x)}`;
};

const ACTION_META = {
    leaveApprovals: { icon: BeachAccessRoundedIcon, color: '#0891B2', to: '/dashboard/leave-management' },
    payslipApprovals: { icon: FactCheckRoundedIcon, color: '#135F93', to: '/dashboard/payslips' },
    overtime: { icon: MoreTimeRoundedIcon, color: '#F59E0B', to: '/dashboard/pay-adjustments?tab=overtime' },
    advances: { icon: SavingsRoundedIcon, color: '#16A34A', to: '/dashboard/pay-adjustments?tab=advances' },
    documentApprovals: { icon: DescriptionRoundedIcon, color: '#0EA5E9', to: '/dashboard/documents?tab=approvals' },
    shiftAssign: { icon: ScheduleRoundedIcon, color: '#E11D48', to: '/dashboard/leave-policy?tab=shifts' },
    resignations: { icon: LogoutRoundedIcon, color: '#DC2626', to: '/dashboard/employees' },
    interviewFeedback: { icon: EventAvailableRoundedIcon, color: '#0E4A75', to: '/dashboard/recruitment?tab=interviews' },
};

const GAP_META = {
    entitySetup: { icon: ApartmentRoundedIcon, color: '#135F93', to: '/dashboard/entities' },
    salaryStructures: { icon: RequestQuoteRoundedIcon, color: '#0EA5E9', to: '/dashboard/payroll-setup?tab=structures' },
    bankDetails: { icon: AccountBalanceRoundedIcon, color: '#16A34A', to: '/dashboard/payroll-setup?tab=bank' },
    noLoginAccess: { icon: AdminPanelSettingsRoundedIcon, color: '#E11D48', to: '/dashboard/employees' },
    probationDue: { icon: HowToRegRoundedIcon, color: '#F59E0B', to: '/dashboard/employees' },
    toRelieve: { icon: LogoutRoundedIcon, color: '#DC2626', to: '/dashboard/employees' },
    awaitingUpload: { icon: DescriptionRoundedIcon, color: '#0891B2', to: '/dashboard/documents?tab=requests' },
    staleInterviews: { icon: RunningWithErrorsRoundedIcon, color: '#0E4A75', to: '/dashboard/recruitment?tab=interviews' },
};

const FALLBACK_META = { icon: TaskAltRoundedIcon, color: '#64748B', to: null };

function ActionTile({ item, meta, onOpen }) {
    const Icon = meta.icon;
    const live = !item.clear && n(item.count) > 0;
    return (
        <Badge
            badgeContent={live ? n(item.count) : 0}
            max={99}
            overlap="rectangular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
                display: 'block', width: '100%', height: '100%',
                '& .MuiBadge-badge': {
                    top: 4, right: 6, minWidth: 20, height: 18, padding: '0 5px', borderRadius: '9px',
                    fontSize: 10, fontWeight: 800, color: '#fff', bgcolor: '#E11D48',
                },
            }}
        >
            <Stack
                direction="row"
                spacing={1.2}
                onClick={meta.to ? onOpen : undefined}
                sx={{
                    alignItems: 'center', p: 1, pr: 1.3, borderRadius: '10px', width: '100%', height: '100%',
                    cursor: meta.to ? 'pointer' : 'default',
                    border: '1px solid', borderColor: live ? `${meta.color}2E` : '#EEF1F6',
                    bgcolor: live ? `${meta.color}0A` : '#FBFBFD',
                    transition: 'border-color .15s, box-shadow .15s, transform .15s',
                    ...(meta.to && { '&:hover': { transform: 'translateY(-1px)', borderColor: `${meta.color}66`, boxShadow: `0 6px 16px -8px ${meta.color}88` } }),
                }}
            >
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0, bgcolor: live ? `${meta.color}18` : '#EEF1F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 17, color: live ? meta.color : '#AAB2C0' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: live ? '#0F172A' : '#64748B', lineHeight: 1.2 }} noWrap>{item.title}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A0AE', lineHeight: 1.2 }} noWrap>{item.subtitle}</Typography>
                </Box>
                {!live && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#86EFAC', flexShrink: 0 }} />}
            </Stack>
        </Badge>
    );
}

function Panel({ title, icon: Icon, action, onAction, color = PRIMARY, children, i = 0 }) {
    return (
        <Box sx={{ ...card, p: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'box-shadow .22s, transform .22s, border-color .22s', '&:hover': { boxShadow: '0 14px 32px -16px rgba(16,24,40,0.24)', borderColor: '#E0EDF6', transform: 'translateY(-2px)' }, ...enter(i) }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2.2, py: 1.7, bgcolor: `${color}12`, borderBottom: `1px solid ${color}24` }}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon sx={{ fontSize: 19, color }} />
                    </Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }}>{title}</Typography>
                </Stack>
                {action && (
                    <Button onClick={onAction} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: PRIMARY, minWidth: 0, px: 1, borderRadius: '7px', '&:hover': { bgcolor: PRIMARY_LIGHT } }}>{action}</Button>
                )}
            </Stack>
            <Box sx={{ p: 2.2, flexGrow: 1 }}>{children}</Box>
        </Box>
    );
}

function Nothing({ icon: Icon, title, hint, color = '#CBD2DD' }) {
    return (
        <Box sx={{ textAlign: 'center', py: 5 }}>
            <Icon sx={{ fontSize: 34, color }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#64748B', mt: 1 }}>{title}</Typography>
            {hint && <Typography sx={{ fontSize: 12, color: '#98A0AE', mt: 0.3 }}>{hint}</Typography>}
        </Box>
    );
}

// One row per required document type, each expanding into the employees
// missing it (newest-hired-first, as the API sends them).
function MissingDocumentsCard({ groups, navigate }) {
    const [openKey, setOpenKey] = useState(null);

    if (!groups.length) {
        return <Nothing icon={DescriptionRoundedIcon} title="No missing KYC documents" hint="Every active employee has Aadhaar and PAN on file." />;
    }

    return (
        <Stack spacing={0.8}>
            {groups.map((g) => {
                const open = openKey === g.documentType;
                const employees = Array.isArray(g.employees) ? g.employees : [];
                return (
                    <Box key={g.documentType} sx={{ border: '1px solid #F3F8FC', borderRadius: '9px', overflow: 'hidden' }}>
                        <Stack
                            direction="row" onClick={() => setOpenKey(open ? null : g.documentType)}
                            sx={{ alignItems: 'center', gap: 1.3, p: 1.2, cursor: 'pointer', '&:hover': { bgcolor: '#FAFAFD' } }}
                        >
                            <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#E4F0F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <DescriptionRoundedIcon sx={{ fontSize: 17, color: '#135F93' }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{g.label}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{g.count} missing</Typography>
                            </Box>
                            <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: '#98A0AE', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                        </Stack>
                        <Collapse in={open}>
                            <Stack sx={{ borderTop: '1px solid #F3F8FC' }}>
                                {employees.map((e, idx) => {
                                    const hasLink = e.id != null;
                                    return (
                                        <Stack
                                            key={`${e.entityId ?? ''}-${e.id ?? idx}`} direction="row"
                                            onClick={() => hasLink && navigate(`/dashboard/employees/${e.id}`)}
                                            sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.6, py: 1, cursor: hasLink ? 'pointer' : 'default', '&:hover': hasLink ? { bgcolor: '#F8FAFC' } : undefined, borderTop: idx === 0 ? 'none' : '1px solid #F7F7FB' }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }} noWrap>{e.name}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{e.employeeCode}{e.entityName ? ` · ${e.entityName}` : ''}</Typography>
                                            </Box>
                                            {hasLink && <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: '#C4CBD6', flexShrink: 0 }} />}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Collapse>
                    </Box>
                );
            })}
        </Stack>
    );
}

export default function DashboardPage() {
    const navigate = useNavigate();
    const auth = useSelector((s) => s.auth);
    const activeEntity = useSelector(selectActiveEntity);
    const activeEntityId = useSelector(selectActiveEntityId);

    const [data, setData] = useState(null);
    const [actions, setActions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const [mainRes, actionRes] = await Promise.allSettled([
            http.get(GetDashboard),
            http.get(GetActionRequired),
        ]);
        try {
            if (mainRes.status === 'rejected') throw mainRes.reason;
            const body = mainRes.value?.data;
            if (body?.error) throw new Error(body.message || 'Could not load the dashboard.');
            setData(body?.data || {});
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load the dashboard.'));
        }
        const actionBody = actionRes.status === 'fulfilled' ? actionRes.value?.data : null;
        setActions(actionBody && !actionBody.error ? actionBody.data || null : null);
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEntityId]);

    useEffect(() => { load(); }, [load]);

    const me = data?.me || {};
    const stats = data?.stats || {};
    const payroll = data?.payroll || {};
    const needs = data?.needsAttention || {};
    const listOf = (k) => (Array.isArray(data?.[k]) ? data[k] : []);
    const interviewsToday = listOf('interviewsToday');
    const lateComers = listOf('lateComers');
    const leaveRequests = listOf('leaveRequests');
    const newJoiners = listOf('newJoiners');
    const birthdays = listOf('birthdays');
    const anniversaries = listOf('anniversaries');
    const trend = listOf('payrollTrend');
    const missingDocuments = listOf('missingDocuments');
    const steps = Array.isArray(payroll.steps) ? payroll.steps : [];
    const doneSteps = steps.filter((s) => s.done);
    const paidComplete = steps.length > 0 && steps.every((s) => s.done);
    const payrollBadge = steps.length
        ? (paidComplete ? (steps[steps.length - 1].label || 'Paid') : (doneSteps[doneSteps.length - 1]?.label || 'In progress'))
        : payroll.statusBadge;

    const isLive = Boolean(me.firstIn) && !me.lastOut;
    const clockedOut = Boolean(me.firstIn) && Boolean(me.lastOut);

    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const start = isLive ? clockToDate(me.firstIn) : null;
        if (!start) { setElapsed(0); return undefined; }
        const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000)));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [isLive, me.firstIn]);
    const fmtTimer = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((x) => String(x).padStart(2, '0')).join(':');

    const workedSeconds = useMemo(() => {
        if (!clockedOut) return 0;
        const a = clockToDate(me.firstIn); const b = clockToDate(me.lastOut);
        if (!a || !b) return 0;
        let diff = Math.floor((b.getTime() - a.getTime()) / 1000);
        if (diff < 0) diff += 24 * 3600;
        return diff;
    }, [clockedOut, me.firstIn, me.lastOut]);
    const timerValue = isLive ? elapsed : (clockedOut ? workedSeconds : 0);
    const hoursMins = (s) => `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
    const hmsToSec = (v) => { const p = String(v || '').split(':').map(Number); return p.some(Number.isNaN) ? 0 : (p[0] || 0) * 3600 + (p[1] || 0) * 60 + (p[2] || 0); };
    const netSeconds = clockedOut ? Math.max(0, workedSeconds - hmsToSec(me.breakDuration)) : 0;
    const clockStatus = isLive
        ? (me.statusLabel || 'Clocked in')
        : clockedOut
            ? `Clocked out · ${hoursMins(workedSeconds)} logged${netSeconds !== workedSeconds ? ` · ${hoursMins(netSeconds)} net` : ''}`
            : (me.statusLabel || 'Not clocked in yet');

    const now = new Date();
    const hr = now.getHours();
    const greet = hr < 12 ? 'morning' : hr < 17 ? 'afternoon' : 'evening';
    const entityName = activeEntity?.name || auth.organisation || 'your organisation';
    const today = me.dateLabel || now.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });

    const total = n(stats.totalEmployees);

    const alerts = [
        { key: 'extendedLeave', icon: BeachAccessRoundedIcon, color: '#F59E0B', bg: '#FFF7ED', title: 'Extended Leave', desc: '3+ continuous days', count: n(needs.extendedLeave) },
        { key: 'lateArrivals', icon: ScheduleRoundedIcon, color: '#E11D48', bg: '#FEE2E2', title: 'Late Arrivals', desc: 'Clocked in after shift start', count: n(needs.lateArrivals) },
        { key: 'absentNoNotice', icon: EventBusyRoundedIcon, color: '#DC2626', bg: '#FEE2E2', title: 'Absent · No Notice', desc: 'Unmarked absence today', count: n(needs.absentNoNotice) },
        { key: 'probationEnding', icon: HowToRegRoundedIcon, color: '#0EA5E9', bg: '#E0F2FE', title: 'Probation Ending', desc: 'Confirmation due this week', count: n(needs.probationEnding) },
        { key: 'documentsPending', icon: DescriptionRoundedIcon, color: '#135F93', bg: '#E4F0F9', title: 'Documents Pending', desc: 'Incomplete onboarding', count: n(needs.documentsPending) },
    ];

    const HIDDEN_ACTION_KEYS = ['resignations'];
    const allActionCards = Array.isArray(actions?.cards) ? actions.cards : [];
    const actionCards = allActionCards.filter((c) => !HIDDEN_ACTION_KEYS.includes(c.key));
    const HIDDEN_GAP_KEYS = ['entitySetup', 'noLoginAccess', 'toRelieve'];
    const actionGaps = (Array.isArray(actions?.gaps) ? actions.gaps : []).filter((g) => !HIDDEN_GAP_KEYS.includes(g.key));
    const hiddenActionCount = allActionCards.filter((c) => HIDDEN_ACTION_KEYS.includes(c.key)).reduce((s, c) => s + n(c.count), 0);
    const totalActions = Math.max(0, n(actions?.totalPending) - hiddenActionCount);
    const totalGaps = actionGaps.reduce((s, g) => s + n(g.count), 0);
    const cal = actions?.workingCalendar || null;

    const absentTotal = n(stats.absentToday) + n(stats.onLeaveToday);
    const totalByRole = toBreakdown(stats.totalByRole ?? stats.employeesByRole ?? stats.byRole);
    const presentByRole = toBreakdown(stats.presentByRole ?? stats.presentTodayByRole);
    const absentByRole = toBreakdown(stats.absentByRole ?? stats.absentTodayByRole);
    const lateByRole = toBreakdown(stats.lateByRole ?? stats.lateComersByRole);
    const KPIS = [
        { label: 'Total Employees', value: total, sub: stats.pastEmployees != null ? `Active · ${n(stats.pastEmployees)} past` : 'Active workforce', icon: GroupsRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT, breakdown: totalByRole, onClick: () => navigate('/dashboard/employees') },
        { label: 'Present Today', value: n(stats.presentToday), sub: `${n(stats.presentPercent)}% attendance`, icon: HowToRegRoundedIcon, color: '#16A34A', bg: '#DCFCE7', breakdown: presentByRole, onClick: () => navigate('/dashboard/attendance-leave?tab=overview') },
        { label: 'Absent Today', value: absentTotal, sub: `${n(stats.absentToday)} absent · ${n(stats.onLeaveToday)} on leave`, icon: EventBusyRoundedIcon, color: '#F59E0B', bg: '#FFF7ED', breakdown: absentByRole, onClick: () => navigate('/dashboard/attendance-leave?tab=overview') },
        { label: 'Late Comers Today', value: n(stats.lateComersToday), sub: stats.latestLateLabel || 'Everyone on time', icon: RunningWithErrorsRoundedIcon, color: '#E11D48', bg: '#FEE2E2', breakdown: lateByRole, onClick: () => navigate('/dashboard/attendance-leave?tab=overview') },
    ];

    const quickActions = [
        { label: 'Onboard Employee', icon: PersonAddAlt1RoundedIcon, to: '/dashboard/employees/onboard', color: '#135F93' },
        { label: 'Run Payroll', icon: FactCheckRoundedIcon, to: '/dashboard/run-payroll', color: '#16A34A' },
        { label: 'Salary Structures', icon: RequestQuoteRoundedIcon, to: '/dashboard/payroll-setup?tab=structures', color: '#0EA5E9' },
        { label: 'Mark Attendance', icon: HowToRegRoundedIcon, to: '/dashboard/attendance-leave?tab=attendance', color: '#F59E0B' },
        { label: 'Leave Management', icon: BeachAccessRoundedIcon, to: '/dashboard/leave-management', color: '#0891B2' },
        { label: 'Roles & Access', icon: AdminPanelSettingsRoundedIcon, to: '/dashboard/roles', color: '#E11D48' },
    ];

    if (loading) return <Loader />;

    if (loadError) {
        return (
            <Box sx={{ p: 2 }}>
                <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                    {loadError}
                </LoadAlert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" sx={{ alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, pb: 2.5 }}>
                <Box>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>Good {greet}, {auth.userName || 'Admin'} 👋</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.2 }}>Here's what's happening at {entityName} today · {today}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip arrow title="Reload">
                        <IconButton onClick={load} sx={{ ...ghostBtn, width: 42, height: 42 }}><RefreshRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                    </Tooltip>
                    <Button onClick={() => navigate('/dashboard/employees/onboard')} startIcon={<PersonAddAlt1RoundedIcon />} sx={{ ...tonalBtn, height: 42, px: 2 }}>Onboard</Button>
                    <Button onClick={() => navigate('/dashboard/run-payroll?tab=register')} startIcon={<PlayArrowRoundedIcon />} sx={{ ...solidBtn, height: 42, px: 2.2 }}>Run Payroll</Button>
                </Stack>
            </Stack>

            <MyAssignedLocations />

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Box sx={{ ...enter(0), background: 'linear-gradient(160deg, #135F93 0%, #0E4A75 100%)', borderRadius: '7px', p: 3, color: '#fff', height: '100%', position: 'relative', overflow: 'hidden', boxShadow: '0 16px 34px -18px rgba(19,95,147,0.7)' }}>
                        <Box sx={{ position: 'absolute', top: -40, right: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', opacity: 0.85, position: 'relative' }}>TODAY</Typography>
                        <Typography sx={{ fontSize: 13.5, opacity: 0.9, mb: 2.2, position: 'relative' }}>{today}</Typography>
                        <Typography sx={{ fontSize: 42, fontWeight: 800, letterSpacing: '1px', lineHeight: 1, position: 'relative' }}>{fmtTimer(timerValue)}</Typography>

                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mt: 1.2, mb: 2.5, position: 'relative' }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: isLive ? '#4ADE80' : clockedOut ? '#38BDF8' : 'rgba(255,255,255,0.6)', boxShadow: isLive ? '0 0 0 3px rgba(74,222,128,0.25)' : 'none' }} />
                            <Typography sx={{ fontSize: 13, opacity: 0.9 }}>{clockStatus}</Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mb: 1.2, position: 'relative' }}>
                            {[
                                ['BREAK', me.breakDuration || '00:00:00'],
                                ['SHIFT', me.shiftName || (me.shiftStart ? `${fmtClock(me.shiftStart)}` : '—')],
                                ['OT', me.otDuration || '00:00:00'],
                            ].map(([l, v]) => (
                                <Box key={l} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.14)', borderRadius: '7px', p: 1.2, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, opacity: 0.8 }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, mt: 0.3 }} noWrap>{v}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ position: 'relative' }}>
                            {[['LOGIN', me.firstIn], ['LOGOUT', me.lastOut]].map(([l, v]) => (
                                <Box key={l} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.14)', borderRadius: '7px', p: 1.2 }}>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, opacity: 0.8 }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, mt: 0.3 }}>{v ? fmtClock(v) : '—'}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mt: 2.2, pt: 1.6, borderTop: '1px solid rgba(255,255,255,0.18)', opacity: 0.85, position: 'relative' }}>
                            <DevicesRoundedIcon sx={{ fontSize: 14 }} />
                            <Typography sx={{ fontSize: 11.5 }}>Login via biometric device, web, or the ARA HumanSync mobile app</Typography>
                        </Stack>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, lg: 8 }}>
                    <Grid container spacing={1.5} sx={{ height: '100%' }}>
                        {KPIS.map((k, i) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={k.label}>
                                <Box onClick={k.onClick} sx={{ ...card, p: 2.5, bgcolor: k.bg, border: `1px solid ${k.color}22`, cursor: 'pointer', height: '100%', transition: 'transform .15s, box-shadow .15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${k.color}22` }, ...enter(i + 1) }}>
                                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: k.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</Typography>
                                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0F172A', mt: 0.5 }}>{k.value}</Typography>
                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280', mt: 0.3 }} noWrap>{k.sub}</Typography>
                                        </Box>
                                        <Box sx={{ width: 36, height: 36, borderRadius: '7px', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <k.icon sx={{ color: k.color, fontSize: 22 }} />
                                        </Box>
                                    </Stack>
                                    <RoleBreakdown items={k.breakdown} color={k.color} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={12}>
                    <Panel
                        title="Action Required"
                        icon={TaskAltRoundedIcon}
                        color="#E11D48"
                        action={totalActions > 0 ? 'Clear your queue' : undefined}
                        onAction={() => navigate('/dashboard/documents?tab=approvals')}
                        i={3}
                    >
                        {!actions ? (
                            <Nothing icon={TaskAltRoundedIcon} title="Couldn't load your queue" hint="The rest of the dashboard is unaffected — reload to try again." />
                        ) : (
                            <>
                                <Typography sx={{ fontSize: 12.5, color: '#64748B', mb: 1.6 }}>
                                    {totalActions > 0
                                        ? <>You have <b style={{ color: '#0F172A' }}>{totalActions}</b> item{totalActions > 1 ? 's' : ''} waiting on you.</>
                                        : 'You\'re all caught up — nothing needs approval right now.'}
                                </Typography>

                                <Grid container spacing={1}>
                                    {actionCards.map((a) => {
                                        const meta = ACTION_META[a.key] || FALLBACK_META;
                                        return (
                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={a.key}>
                                                <ActionTile item={a} meta={meta} onOpen={() => navigate(meta.to)} />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {actionGaps.length > 0 && (
                                    <Box sx={{ mt: 2, pt: 1.8, borderTop: '1px solid #F3F8FC' }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.2 }}>
                                            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Setup Gaps</Typography>
                                            {totalGaps > 0 && <Chip label={totalGaps} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 800, bgcolor: '#FEF3C7', color: '#B45309' }} />}
                                        </Stack>
                                        <Grid container spacing={1}>
                                            {actionGaps.map((g) => {
                                                const meta = GAP_META[g.key] || FALLBACK_META;
                                                return (
                                                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={g.key}>
                                                        <ActionTile item={g} meta={meta} onOpen={() => navigate(meta.to)} />
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                )}

                                {cal && (
                                    <Stack
                                        direction="row"
                                        onClick={() => navigate('/dashboard/leave-policy?tab=calendar')}
                                        sx={{
                                            mt: 1.6, alignItems: 'center', gap: 1.5, p: 1.6, borderRadius: '10px', cursor: 'pointer',
                                            bgcolor: cal.isSet ? '#F5FDF8' : (cal.overdue ? '#FEF2F2' : '#FFF7ED'),
                                            border: `1px solid ${cal.isSet ? '#DCFCE7' : (cal.overdue ? '#FECDD3' : '#FCEBD0')}`,
                                            transition: 'background-color .15s',
                                            '&:hover': { bgcolor: cal.isSet ? '#ECFDF3' : (cal.overdue ? '#FEE2E2' : '#FEF0DC') },
                                        }}
                                    >
                                        <Box sx={{ width: 40, height: 40, borderRadius: '10px', flexShrink: 0, bgcolor: cal.isSet ? '#DCFCE7' : (cal.overdue ? '#FEE2E2' : '#FEF3C7'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CalendarMonthRoundedIcon sx={{ fontSize: 21, color: cal.isSet ? '#16A34A' : (cal.overdue ? '#E11D48' : '#B45309') }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>Working Calendar · {cal.title || cal.monthName}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#64748B' }} noWrap>{cal.note}</Typography>
                                        </Box>
                                        {(cal.daysLeftLabel || cal.daysLeft != null) && (
                                            <Chip
                                                label={cal.daysLeftLabel || `${n(cal.daysLeft)}d left`}
                                                size="small"
                                                sx={{ height: 24, fontSize: 11, fontWeight: 800, flexShrink: 0, bgcolor: cal.isSet ? '#DCFCE7' : (cal.overdue ? '#FEE2E2' : '#FEF3C7'), color: cal.isSet ? '#16A34A' : (cal.overdue ? '#E11D48' : '#B45309') }}
                                            />
                                        )}
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); navigate('/dashboard/leave-policy?tab=calendar'); }}
                                            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
                                            sx={{ ...tonalBtn, height: 34, px: 1.6, display: { xs: 'none', sm: 'inline-flex' } }}
                                        >
                                            {cal.isSet ? 'View' : 'Set calendar'}
                                        </Button>
                                    </Stack>
                                )}
                            </>
                        )}
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Panel title="Interviews Today" icon={EventAvailableRoundedIcon} color="#0EA5E9" action="Recruitment" onAction={() => navigate('/dashboard/recruitment?tab=interviews')} i={4}>
                        {interviewsToday.length === 0 ? (
                            <Nothing icon={EventAvailableRoundedIcon} title="No interviews today" hint="Schedule one from Recruitment." />
                        ) : (
                            <Stack spacing={0.6}>
                                {interviewsToday.map((iv, idx) => {
                                    const name = iv.candidateName || iv.name || '—';
                                    const outcome = iv.outcome || null;
                                    const toReview = iv.status === 'Conducted' && !outcome;
                                    const tone = outcome
                                        ? (outcome === 'Selected' ? { c: '#16A34A', bg: '#DCFCE7' } : outcome === 'Rejected' ? { c: '#E11D48', bg: '#FEE2E2' } : { c: '#B45309', bg: '#FFF7ED' })
                                        : toReview ? { c: '#B45309', bg: '#FFF7ED' } : { c: '#64748B', bg: '#F1F5F9' };
                                    return (
                                        <Stack
                                            key={iv.id ?? idx}
                                            direction="row"
                                            onClick={() => navigate('/dashboard/recruitment?tab=interviews')}
                                            sx={{ alignItems: 'center', gap: 1.2, p: 1, borderRadius: '9px', cursor: 'pointer', transition: 'background-color .15s', '&:hover': { bgcolor: '#F8FAFC' } }}
                                        >
                                            <Box sx={{ minWidth: 62, textAlign: 'center', px: 0.8, py: 0.7, borderRadius: '8px', bgcolor: '#E0F2FE', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0369A1', lineHeight: 1.2 }}>{fmtClock(iv.time || iv.scheduledTime)}</Typography>
                                                {Boolean(iv.durationMins || iv.duration) && (
                                                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#0EA5E9' }}>{iv.durationMins || iv.duration}m</Typography>
                                                )}
                                            </Box>
                                            <Avatar sx={{ width: 34, height: 34, ...avatarTone(name), fontSize: 12, fontWeight: 700 }}>{iv.initials || initials(name)}</Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{name}</Typography>
                                                <Typography sx={{ fontSize: 10.5, color: '#94A3B8' }} noWrap>{[iv.round, iv.vacancyTitle || iv.roleTitle].filter(Boolean).join(' · ')}</Typography>
                                            </Box>
                                            <Chip
                                                label={outcome || (toReview ? 'To review' : iv.mode || 'Scheduled')}
                                                size="small"
                                                sx={{ height: 19, fontSize: 9.5, fontWeight: 700, flexShrink: 0, bgcolor: tone.bg, color: tone.c }}
                                            />
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Panel title="Late Comers Today" icon={RunningWithErrorsRoundedIcon} color="#E11D48" action="Attendance" onAction={() => navigate('/dashboard/attendance-leave?tab=overview')} i={5}>
                        {lateComers.length === 0 ? (
                            <Nothing icon={CheckCircleRoundedIcon} color="#BBF7D0" title="Everyone was on time" hint="No punches after the shift start." />
                        ) : (
                            <Stack spacing={0.6}>
                                {lateComers.map((p, idx) => {
                                    const name = p.name || '—';
                                    return (
                                        <Stack key={p.id ?? idx} direction="row" sx={{ alignItems: 'center', gap: 1.2, p: 1, borderRadius: '9px', transition: 'background-color .15s', '&:hover': { bgcolor: '#FFF7F8' } }}>
                                            <Avatar sx={{ width: 34, height: 34, bgcolor: `${colorFor(name)}22`, color: colorFor(name), fontSize: 12, fontWeight: 700 }}>{p.initials || initials(name)}</Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{name}</Typography>
                                                <Typography sx={{ fontSize: 10.5, color: '#94A3B8' }} noWrap>{p.department || p.dept || p.subtitle || '—'}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>{fmtClock(p.inTime || p.firstIn)}</Typography>
                                                {Boolean(p.lateByLabel || p.lateBy) && (
                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#E11D48' }}>{p.lateByLabel || p.lateBy} late</Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Panel title="Payroll Status" icon={PaymentsRoundedIcon} color="#135F93" i={5}>
                        {!payroll.hasCycle ? (
                            <Nothing icon={PaymentsRoundedIcon} title="No payroll cycle yet" hint="Start this month's run from Run Payroll." />
                        ) : (
                            <>
                                <Typography sx={{ fontSize: 11.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{payroll.cycleLabel || 'Current cycle'}</Typography>
                                <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', mt: 0.5, gap: 1 }}>
                                    <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>₹{n(payroll.grossPayout).toLocaleString('en-IN')}</Typography>
                                    {payrollBadge && <Chip label={payrollBadge} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, flexShrink: 0, bgcolor: paidComplete ? '#DCFCE7' : '#FEF3C7', color: paidComplete ? '#16A34A' : '#B45309' }} />}
                                </Stack>
                                <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.3 }}>
                                    Gross payout · {n(payroll.employees)} employee{n(payroll.employees) === 1 ? '' : 's'} · net ₹{n(payroll.netPayout).toLocaleString('en-IN')}
                                </Typography>
                                <Stack spacing={0} sx={{ mt: 2 }}>
                                    {steps.map((s) => (
                                        <Stack key={s.label} direction="row" spacing={1.2} sx={{ alignItems: 'center', py: 0.5 }}>
                                            {s.done ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: PRIMARY }} /> : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 18, color: s.current ? PRIMARY : '#CBD2DD' }} />}
                                            <Typography sx={{ fontSize: 12.5, fontWeight: s.done || s.current ? 700 : 500, color: s.done || s.current ? '#0F172A' : '#94A3B8' }}>{s.label}</Typography>
                                            {s.current && <Chip label="Current" size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }} />}
                                        </Stack>
                                    ))}
                                </Stack>
                                <Button fullWidth onClick={() => navigate('/dashboard/payslips')} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...tonalBtn, mt: 1.5, height: 38 }}>Continue Payroll</Button>
                            </>
                        )}
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={12}>
                    <Panel title="Quick Actions" icon={PlayArrowRoundedIcon} color="#0EA5E9" i={6}>
                        <Grid container spacing={1}>
                            {quickActions.map((a) => (
                                <Grid size={{ xs: 6, sm: 4, lg: 2 }} key={a.label}>
                                    <Stack onClick={() => navigate(a.to)} spacing={0.8} sx={{ alignItems: 'flex-start', p: 1.4, borderRadius: '8px', border: '1px solid #EEF1F6', bgcolor: '#F8FAFC', cursor: 'pointer', height: '100%', transition: 'all .15s', '&:hover': { borderColor: `${a.color}55`, bgcolor: `${a.color}0D`, transform: 'translateY(-1px)' } }}>
                                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${a.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <a.icon sx={{ fontSize: 18, color: a.color }} />
                                        </Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155', lineHeight: 1.25 }}>{a.label}</Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Panel title="Payroll Cost Trend" icon={PaymentsRoundedIcon} color="#135F93" action="Salary Register" onAction={() => navigate('/dashboard/run-payroll?tab=register')} i={7}>
                        {trend.length === 0 ? (
                            <Nothing icon={PaymentsRoundedIcon} title="No payroll history yet" hint="The trend builds up as cycles are run." />
                        ) : (
                            <BarChart
                                xAxis={[{ scaleType: 'band', data: trend.map((t) => t.month), categoryGapRatio: 0.55, barGapRatio: 0.3 }]}
                                yAxis={[{ valueFormatter: compactInr, width: 56 }]}
                                series={[
                                    { data: trend.map((t) => n(t.gross)), label: 'Gross', color: '#135F93', valueFormatter: (v) => `₹${n(v).toLocaleString('en-IN')}` },
                                    { data: trend.map((t) => n(t.net)), label: 'Net', color: '#BDDFF6', valueFormatter: (v) => `₹${n(v).toLocaleString('en-IN')}` },
                                ]}
                                height={250}
                                borderRadius={6}
                                grid={{ horizontal: true }}
                                margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                                slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'top', horizontal: 'end' } } }}
                                sx={{ '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { display: 'none' }, '& .MuiChartsGrid-line': { stroke: '#EEF1F6' }, '& .MuiChartsAxis-tickLabel': { fontSize: 11, fill: '#94A3B8' } }}
                            />
                        )}
                    </Panel>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Panel title="Needs Attention" icon={WarningAmberRoundedIcon} color="#F59E0B" i={8}>
                        <Stack spacing={0.5}>
                            {alerts.map((a) => (
                                <Stack key={a.key} direction="row" sx={{ alignItems: 'center', gap: 1.3, p: 1, borderRadius: '9px', transition: 'background-color .15s', opacity: a.count ? 1 : 0.6, '&:hover': { bgcolor: '#F8FAFC' } }}>
                                    <Box sx={{ width: 36, height: 36, borderRadius: '9px', bgcolor: a.count ? a.bg : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <a.icon sx={{ fontSize: 19, color: a.count ? a.color : '#94A3B8' }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{a.title}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>{a.desc}</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: 26, height: 24, px: 0.9, borderRadius: '7px', bgcolor: a.count ? a.bg : '#F1F5F9', color: a.count ? a.color : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{a.count}</Typography>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={12}>
                    <Panel title="Missing Documents" icon={DescriptionRoundedIcon} color="#135F93" action="View employees" onAction={() => navigate('/dashboard/employees')} i={8}>
                        <MissingDocumentsCard groups={missingDocuments} navigate={navigate} />
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Panel title="Leave Requests" icon={BeachAccessRoundedIcon} color="#0891B2" action="View all" onAction={() => navigate('/dashboard/leave-management')} i={7}>
                        {leaveRequests.length === 0 ? (
                            <Nothing icon={BeachAccessRoundedIcon} title="No leave requests" hint="Nothing is waiting on a decision." />
                        ) : (
                            <Stack spacing={0.4}>
                                {leaveRequests.map((lr, idx) => {
                                    const tone = lr.colorTag || colorFor(lr.name || '');
                                    return (
                                        <Stack key={lr.id ?? idx} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: '9px', transition: 'background-color .15s', '&:hover': { bgcolor: '#F8FAFC' } }}>
                                            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                <Avatar sx={{ width: 38, height: 38, bgcolor: `${tone}22`, color: tone, fontSize: 12.5, fontWeight: 700, border: `2px solid ${tone}33` }}>{lr.initials || initials(lr.name || '')}</Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{lr.name}</Typography>
                                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', mt: 0.2 }}>
                                                        <Chip label={lr.leaveType} size="small" sx={{ height: 17, fontSize: 9.5, fontWeight: 700, bgcolor: `${tone}18`, color: tone, '& .MuiChip-label': { px: 0.7 } }} />
                                                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>{[lr.daysLabel, lr.dateRange].filter(Boolean).join(' · ')}</Typography>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                            <Stack direction="row" spacing={0.6} sx={{ flexShrink: 0 }}>
                                                <Tooltip arrow title="Review in Leave Management">
                                                    <IconButton size="small" onClick={() => navigate('/dashboard/leave-management')} sx={{ bgcolor: '#DCFCE7', borderRadius: '7px', width: 30, height: 30, '&:hover': { bgcolor: '#BBF7D0' } }}><CheckRoundedIcon sx={{ fontSize: 16, color: '#16A34A' }} /></IconButton>
                                                </Tooltip>
                                                <Tooltip arrow title="Review in Leave Management">
                                                    <IconButton size="small" onClick={() => navigate('/dashboard/leave-management')} sx={{ bgcolor: '#FEE2E2', borderRadius: '7px', width: 30, height: 30, '&:hover': { bgcolor: '#FECACA' } }}><CloseRoundedIcon sx={{ fontSize: 16, color: '#DC2626' }} /></IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                    <Panel title="New Joiners" icon={PersonAddAlt1RoundedIcon} color="#16A34A" i={8}>
                        <Grid container spacing={2}>
                            {[
                                { rows: newJoiners, label: 'New Joiners', icon: PersonAddAlt1RoundedIcon, colour: '#16A34A', bg: '#F5FDF8', border: '#DCFCE7', chipBg: '#DCFCE7', empty: 'No recent joiners' },
                            ].map((col) => (
                                <Grid size={{ xs: 12 }} key={col.label}>
                                    <Box sx={{ bgcolor: col.bg, border: `1px solid ${col.border}`, borderRadius: '10px', p: 1.4, height: '100%' }}>
                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mb: 1.2 }}>
                                            <col.icon sx={{ fontSize: 16, color: col.colour }} />
                                            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: col.colour, textTransform: 'uppercase', letterSpacing: 0.3 }}>{col.label}</Typography>
                                            <Chip label={col.rows.length} size="small" sx={{ height: 17, fontSize: 10, fontWeight: 800, bgcolor: col.chipBg, color: col.colour, '& .MuiChip-label': { px: 0.7 } }} />
                                        </Stack>
                                        <Stack spacing={0.3}>
                                            {col.rows.map((p, idx) => (
                                                <Stack key={`${p.name}-${idx}`} direction="row" spacing={1.2} sx={{ alignItems: 'center', p: 0.7, borderRadius: '8px', transition: 'background-color .15s', '&:hover': { bgcolor: '#fff' } }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${colorFor(p.name || '')}22`, color: colorFor(p.name || ''), fontSize: 11.5, fontWeight: 700 }}>{p.initials || initials(p.name || '')}</Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{p.name}</Typography>
                                                        <Typography sx={{ fontSize: 10.5, color: '#94A3B8' }} noWrap>{[p.subtitle, p.whenLabel].filter(Boolean).join(' · ')}</Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                            {col.rows.length === 0 && <Typography sx={{ fontSize: 12, color: '#B4BBC6', p: 0.7 }}>{col.empty}</Typography>}
                                        </Stack>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Panel title="Upcoming Birthdays" icon={CakeRoundedIcon} color="#E11D48" i={9}>
                        {birthdays.length === 0 ? (
                            <Nothing icon={CakeRoundedIcon} title="No birthdays coming up" />
                        ) : (
                            <Stack spacing={0.4}>
                                {birthdays.map((p, idx) => {
                                    const isToday = String(p.whenLabel || '').toLowerCase() === 'today';
                                    return (
                                        <Stack key={`${p.name}-${idx}`} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: '9px', transition: 'background-color .15s', '&:hover': { bgcolor: '#FDF2F8' } }}>
                                            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                <Avatar sx={{ width: 38, height: 38, bgcolor: `${colorFor(p.name || '')}22`, color: colorFor(p.name || ''), fontSize: 12.5, fontWeight: 700, border: `2px solid ${colorFor(p.name || '')}33` }}>{p.initials || initials(p.name || '')}</Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{p.name}</Typography>
                                                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>{p.subtitle || '—'}</Typography>
                                                </Box>
                                            </Stack>
                                            <Chip icon={<CakeRoundedIcon sx={{ fontSize: '13px !important' }} />} label={p.whenLabel || '—'} size="small" sx={{ height: 23, fontSize: 11, fontWeight: 700, bgcolor: isToday ? '#FCE7F3' : '#F1F5F9', color: isToday ? '#DB2777' : '#64748B', '& .MuiChip-icon': { color: isToday ? '#DB2777' : '#94A3B8' } }} />
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Panel>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Panel title="Work Anniversaries" icon={EmojiEventsRoundedIcon} color="#F59E0B" i={10}>
                        {anniversaries.length === 0 ? (
                            <Nothing icon={EmojiEventsRoundedIcon} title="No anniversaries coming up" />
                        ) : (
                            <Stack spacing={0.4}>
                                {anniversaries.map((p, idx) => (
                                    <Stack key={`${p.name}-${idx}`} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: '9px', transition: 'background-color .15s', '&:hover': { bgcolor: '#FFFBF3' } }}>
                                        <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                            <Avatar sx={{ width: 38, height: 38, bgcolor: `${colorFor(p.name || '')}22`, color: colorFor(p.name || ''), fontSize: 12.5, fontWeight: 700, border: `2px solid ${colorFor(p.name || '')}33` }}>{p.initials || initials(p.name || '')}</Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{p.name}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>{p.subtitle || '—'}</Typography>
                                            </Box>
                                        </Stack>
                                        <Chip icon={<EmojiEventsRoundedIcon sx={{ fontSize: '13px !important' }} />} label={p.whenLabel || '—'} size="small" sx={{ height: 23, fontSize: 11, fontWeight: 700, bgcolor: '#FFF7ED', color: '#B45309', '& .MuiChip-icon': { color: '#F59E0B' } }} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Panel>
                </Grid>
            </Grid>
        </Box>
    );
}
